<template>
    <section class="authior-biography-slice">
        <template v-if="isArticleOrSuccessStory(article)">
            <AuthorInfo />
            <PrismicRichText v-if="isFilled.richText(slice.primary.authorBiography)" :field="slice.primary.authorBiography" class="biography" />

            <div class="links">
                <div class="socials">
                    <template v-for="(item, index) in slice.items">
                        <SocialMediaLink
                            v-if="item.socialProfileType"
                            :key="`author-${index}`"
                            :link="item.socialProfileLink"
                            :social-media="item.socialProfileType"
                        />
                    </template>
                </div>
                <a
                    v-if="slice.primary.authorProfileLink && slice.primary.authorProfileLink !== ''"
                    :href="$withLocalePrefix(slice.primary.authorProfileLink)"
                    class="profile-link"
                >
                    <BrewIcon v-if="isMaltProfile" class="brew-icon" />
                    {{ isMaltProfile ? t('resources.author.seeProfileOnMalt') : t('resources.author.seeProfile') }}
                </a>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
    import {ref, inject} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {PrismicDocument} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {AuthorBiographySlice, ArticleDocument, SuccessStoryDocument} from '@prismicio-types';
    import {useTranslation} from '#imports';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import AuthorInfo from '~/components/AuthorInfo.vue';
    import BrewIcon from '~/components/icons/Brew.vue';
    import SocialMediaLink from '~/components/SocialMediaLink.vue';

    const {slice} = defineProps(getSliceComponentProps<AuthorBiographySlice>());
    const article = inject(PRISMIC_DOCUMENT);
    const {t} = useTranslation();
    function isArticleOrSuccessStory(article: PrismicDocument | null | undefined): article is ArticleDocument | SuccessStoryDocument {
        return article?.type === 'article' || article?.type === 'successStory';
    }

    const isMaltProfile = ref(slice.primary.isMaltProfile === null || slice.primary.isMaltProfile === true);
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    .authior-biography-slice {
        padding: var(--joy-core-spacing-9);
        border: solid 1px var(--joy-color-neutral-30);
        .biography {
            margin-top: var(--joy-core-spacing-4);
        }
        .links,
        .socials {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
        }
        .links {
            margin-top: var(--joy-core-spacing-7);
            justify-content: space-between;
            @media (max-width: $mobile-breakpoint) {
                flex-direction: column;
                align-items: flex-start;
                gap: var(--joy-core-spacing-7);
            }
        }
        .brew-icon {
            margin-right: var(--joy-core-spacing-1);
        }
        .profile-link {
            display: flex;
            align-items: center;
            color: var(--joy-color-primary-50);
            transition: color var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            svg * {
                fill: var(--joy-color-primary-50);
                transition: fill var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            }
            &:hover {
                color: var(--joy-color-primary-70);
                svg * {
                    fill: var(--joy-color-primary-70);
                }
            }
        }
    }
</style>
